import React, { useState } from 'react';
import { Film } from 'lucide-react';
import { formatDateTime } from '../utils/editorVideoFormat';

export const GRID_COLUMNS = 3;
export const CAROUSEL_MAX_ITEMS = 6;

/** All completed videos, newest first. */
const selectCompletedVideos = (jobs) =>
  (Array.isArray(jobs) ? jobs : [])
    .filter((job) => job && job.status === 'COMPLETED')
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

/** Last completed videos, newest first, capped to the grid size. */
export const selectCarouselVideos = (jobs) =>
  selectCompletedVideos(jobs).slice(0, CAROUSEL_MAX_ITEMS);

const Placeholder = () => (
  <div
    data-testid="thumbnail-placeholder"
    className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-300"
  >
    <Film className="w-8 h-8" />
  </div>
);

/**
 * How many tiles to render: 6 fills a 3x2 grid, 4-5 collapse to a single row
 * (the rest lives behind "Ver todos"), and 1-3 are shown as they are.
 */
export const visibleCount = (total) => {
  if (total >= CAROUSEL_MAX_ITEMS) return CAROUSEL_MAX_ITEMS;
  if (total > GRID_COLUMNS) return GRID_COLUMNS;
  return total;
};

/**
 * Static grid of thumbnails (plain <img>, never <video>, so no video bytes are
 * downloaded). Tiles keep a fixed 1/3 width so 1-2 videos stay centered at a
 * size where the low-res thumbnail still looks sharp. Falls back to a neutral
 * placeholder when a thumbnail is missing (videos rendered before thumbnails
 * existed) or fails to load.
 */
const EditorVideosCarousel = ({ jobs, onViewAll }) => {
  const videos = selectCompletedVideos(jobs);
  const [failedIds, setFailedIds] = useState({});

  if (videos.length === 0) return null;

  const tiles = videos.slice(0, visibleCount(videos.length));
  const hasMore = videos.length > tiles.length;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-600">Últimos creados</h3>
      <div className="flex flex-wrap justify-center gap-3">
        {tiles.map((job) => {
          const showImage = job.thumbnailUrl && !failedIds[job.id];
          return (
            <div
              key={job.id}
              className="relative aspect-video overflow-hidden rounded-xl border border-gray-200 bg-gray-100 w-[calc((100%-1.5rem)/3)]"
            >
              {showImage ? (
                <img
                  src={job.thumbnailUrl}
                  alt={`Video creado el ${formatDateTime(job.createdAt)}`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  onError={() => setFailedIds((prev) => ({ ...prev, [job.id]: true }))}
                />
              ) : (
                <Placeholder />
              )}
              <p className="absolute bottom-0 inset-x-0 px-2 py-1 text-[11px] text-white bg-gradient-to-t from-black/60 to-transparent">
                {formatDateTime(job.createdAt)}
              </p>
            </div>
          );
        })}
      </div>

      {hasMore && onViewAll && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onViewAll}
            className="px-4 py-1.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Ver todos
          </button>
        </div>
      )}
    </div>
  );
};

export default EditorVideosCarousel;
